import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/auth-context';
import StepUpAuth from '../pages/settings/stepupauth/StepUpAuth';
import HistoryBar from './HistoryBar';
import useFetchFarms from '../hooks/useFetchFarms';

interface FarmData {
  farm_id: string;
  farm_name: string;
  country?: string;
  county?: string;
  region?: string;
}

interface AppLayoutProps {
  children: React.ReactNode;
  breadcrumb?: {
    parent: string;
    current: string;
  };
  currentPage?: string;
  dynamicLinks?: Array<{
    id: string;
    label: string;
    to: string;
    icon?: string;
  }>;
  farms?: FarmData[];
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children, breadcrumb, currentPage, dynamicLinks, farms: farmsProp = [] }) => {
  const { signOut, toggleShowAuthenticatorManager, signInStatus, idToken, userRole } = useAuthContext();
  const [showStepUpAuth] = useState(false);
  const [farms, setFarms] = useState<FarmData[]>(farmsProp);
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [userOrgName, setUserOrgName] = useState<string | null>(null);

  // Function to close sidebar on mobile
  const closeSidebar = () => {
    const body = document.body;
    body.classList.remove('g-sidenav-pinned');
    body.classList.add('g-sidenav-hidden');
    setIsSidebarOpen(false);
  };

  // Fetch farms globally for navigation
  useFetchFarms(idToken, signInStatus, (farmsData: FarmData[]) => {
    if (farmsData && Array.isArray(farmsData) && farmsData.length > 0) {
      setFarms(farmsData);
    } else {
      console.log('No farms data or empty array. Data:', farmsData);
    }
  });

  // Fetch user's organization name
  useEffect(() => {
    const fetchUserOrganization = async () => {
      if (signInStatus !== 'SIGNED_IN' || !idToken) {
        setUserOrgName(null);
        return;
      }

      try {
        // Get user's org_id
        const userResponse = await fetch(
          `${import.meta.env.VITE_AWS_LAMBDA_API}get-user`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
          }
        );

        if (!userResponse.ok) {
          console.log('Could not fetch user info');
          return;
        }

        const userData = await userResponse.json();
        const userInfo = typeof userData === 'object' && userData.data ? userData.data : userData;
        const orgId = userInfo.org_id;

        if (!orgId) {
          return;
        }

        // Get all organizations to find the matching one
        const orgsResponse = await fetch(
          `${import.meta.env.VITE_AWS_LAMBDA_API}list-organizations`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
          }
        );

        if (!orgsResponse.ok) {
          console.log('Could not fetch organizations');
          return;
        }

        const orgsData = await orgsResponse.json();
        const organizations = typeof orgsData === 'object' && orgsData.data ? orgsData.data : [];
        
        // Find the organization that matches the user's org_id
        // Handle both UUID org_id and 'ORG#GENERAL' string
        const org = organizations.find((o: any) => 
          o.org_id === orgId || 
          o.pk === orgId || 
          (orgId === 'ORG#GENERAL' && o.pk === 'ORG#GENERAL')
        );

        if (org) {
          setUserOrgName(org.name);
        } else {
          // If not found, use the org_id as fallback
          setUserOrgName(orgId);
        }
      } catch (error) {
        console.error('Error fetching user organization:', error);
      }
    };

    fetchUserOrganization();
  }, [signInStatus, idToken]);

  // Update farms when prop changes (prop takes precedence)
  useEffect(() => {
    if (farmsProp.length > 0) {
      setFarms(farmsProp);
    }
  }, [farmsProp]);

  // Handle window resize to auto-collapse navbar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      const body = document.body;
      if (window.innerWidth < 1200) {
        // Small screen - ensure navbar is collapsed/hidden
        setIsNavbarCollapsed(true);
        setIsMobile(true);
        body.classList.remove('g-sidenav-pinned');
        body.classList.add('g-sidenav-hidden');
      } else {
        // Large screen - show sidebar
        setIsNavbarCollapsed(false);
        setIsMobile(false);
        body.classList.add('g-sidenav-pinned');
        body.classList.remove('g-sidenav-hidden');
      }
    };

    // Set initial state based on screen size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debug: Log current farms state
  useEffect(() => {
    //far,s
  }, [farms]);


  // Handle hamburger menu functionality
  useEffect(() => {
    const handleHamburgerClick = (e: Event) => {
      e.preventDefault();
      const body = document.body;
      body.classList.toggle('g-sidenav-pinned');
      if (body.classList.contains('g-sidenav-pinned')) {
        body.classList.remove('g-sidenav-hidden');
        body.classList.add('g-sidenav-show');
        setIsSidebarOpen(true);
      } else {
        body.classList.remove('g-sidenav-show');
        body.classList.add('g-sidenav-hidden');
        setIsSidebarOpen(false);
      }
    };

    const handleCloseClick = (e: Event) => {
      e.preventDefault();
      const body = document.body;
      body.classList.remove('g-sidenav-show');
      body.classList.add('g-sidenav-hidden');
    };

    // Add event listeners
    const desktopHamburger = document.getElementById('desktopHamburger');
    const mobileHamburger = document.getElementById('iconNavbarSidenav');
    const closeButton = document.getElementById('iconSidenav');

    if (desktopHamburger) {
      desktopHamburger.addEventListener('click', handleHamburgerClick);
    }

    if (mobileHamburger) {
      mobileHamburger.addEventListener('click', handleHamburgerClick);
    }

    if (closeButton) {
      closeButton.addEventListener('click', handleCloseClick);
    }

    // Cleanup
    return () => {
      if (desktopHamburger) {
        desktopHamburger.removeEventListener('click', handleHamburgerClick);
      }
      if (mobileHamburger) {
        mobileHamburger.removeEventListener('click', handleHamburgerClick);
      }
      if (closeButton) {
        closeButton.removeEventListener('click', handleCloseClick);
      }
    };
  }, []);

  return (
    <div className="g-sidenav-show">
      {/* Mobile overlay - click to close sidebar */}
      {isSidebarOpen && isMobile && (
        <div
          className="sidebar-overlay"
          onClick={closeSidebar}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            cursor: 'pointer',
          }}
        />
      )}
      {/* Sidebar */}
      <aside className="sidenav navbar navbar-vertical navbar-expand-xs border-radius-lg fixed-start ms-2 bg-deep-blue my-2" id="sidenav-main">
        <div className="sidenav-header">
          <button
            className="fas fa-times p-3 cursor-pointer text-dark opacity-5 position-absolute end-0 top-0 d-none d-xl-none border-0 bg-transparent"
            aria-hidden="true"
            id="iconSidenav"
          ></button>
          <a className="navbar-brand px-4 py-3 m-0" href="/dashboard">
            <img src="/assets/images/logo-PPNAnalyzer-login.png" className="navbar-brand-img" width="250" height="250" alt="PPNAnalyzer" style={{ filter: 'brightness(0) invert(1)' }} />
          </a>
        </div>

        {/* User Organization Display - Fixed at bottom of sidebar */}
        {userOrgName && (
          <>
            <hr className="horizontal light mt-0 mb-2" />
            <div className="px-4 py-3" >
              <div className="d-flex align-items-center">
                <i className="material-symbols-rounded opacity-10 me-2" style={{ fontSize: '20px' }}>corporate_fare</i>
                <div className="flex-grow-1">
                  <p className="text-xs text-white opacity-8 mb-0">Organization</p>
                  <Link 
                    to="/organization/dashboard" 
                    className="text-sm text-white font-weight-bold mb-0 d-block"
                    style={{ textDecoration: 'none', cursor: 'pointer' }}
                    title="View Organization Dashboard"
                  >
                    {userOrgName}
                  </Link>
                </div>
              </div>
            </div>
            <hr className="horizontal light mt-2 mb-2" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.3)', width: '100%' }} />
          </>
        )}

        {!userOrgName && <hr className="horizontal light mt-0 mb-2" />}
        <div className="collapse navbar-collapse w-auto h-auto" id="sidenav-collapse-main">
          <ul className="navbar-nav">
            <hr className="horizontal light mt-0" />
            <li className="nav-item">
              <Link className={`nav-link text-white ${currentPage === 'dashboard' ? 'active bg-gradient-primary' : ''}`} to="/dashboard">
                <i className="material-symbols-rounded opacity-10">space_dashboard</i>
                <span className="nav-link-text ms-1 ps-1">Dashboard</span>
              </Link>
            </li>
            {/* Platform Menu - Only show for SYS_ADMIN */}
            {userRole === 'SYS_ADMIN' && (
              <li className="nav-item">
                <a data-bs-toggle="collapse" href="#platformExamples"
                  className={`nav-link text-white ${currentPage?.startsWith('platform') ? 'active bg-gradient-primary' : ''}`} aria-controls="platformExamples" role="button" aria-expanded={currentPage?.startsWith('platform') ? 'true' : 'false'}>
                  <i className="material-symbols-rounded opacity-10">admin_panel_settings</i>
                  <span className="nav-link-text ms-1 ps-1">Platform</span>
                </a>
                <div className={`collapse ${currentPage?.startsWith('platform') ? 'show' : ''}`} id="platformExamples">
                  <ul className="nav">
                    <li className="nav-item">
                      <Link className={`nav-link text-white ${currentPage === 'platform-organizations' ? 'active' : ''}`} to="/platform/organizations">
                        <span className="sidenav-mini-icon"></span>
                        <span className="sidenav-normal ms-1 ps-1">Organizations</span>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link className={`nav-link text-white ${currentPage === 'platform-users' ? 'active' : ''}`} to="/platform/users">
                        <span className="sidenav-mini-icon"></span>
                        <span className="sidenav-normal ms-1 ps-1">Users</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>
            )}
            {/* Organization Menu - Only show for ORG_ADMIN */}
            {userRole === 'ORG_ADMIN' && (
              <li className="nav-item">
                <a data-bs-toggle="collapse" href="#organizationExamples"
                  className={`nav-link text-white ${currentPage?.startsWith('organization') ? 'active bg-gradient-primary' : ''}`} aria-controls="organizationExamples" role="button" aria-expanded={currentPage?.startsWith('organization') ? 'true' : 'false'}>
                  <i className="material-symbols-rounded opacity-10">corporate_fare</i>
                  <span className="nav-link-text ms-1 ps-1">Organization</span>
                </a>
                <div className={`collapse ${currentPage?.startsWith('organization') ? 'show' : ''}`} id="organizationExamples">
                  <ul className="nav">
                    <li className="nav-item">
                      <Link className={`nav-link text-white ${currentPage === 'organization-dashboard' ? 'active' : ''}`} to="/organization/dashboard">
                        <span className="sidenav-mini-icon"></span>
                        <span className="sidenav-normal ms-1 ps-1">Dashboard</span>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link className={`nav-link text-white ${currentPage === 'organization-users' ? 'active' : ''}`} to="/organization/users">
                        <span className="sidenav-mini-icon"></span>
                        <span className="sidenav-normal ms-1 ps-1">Users</span>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link className={`nav-link text-white ${currentPage === 'organization-membership' ? 'active' : ''}`} to="/organization/membership">
                        <span className="sidenav-mini-icon"></span>
                        <span className="sidenav-normal ms-1 ps-1">Membership</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>
            )}
            
            <li className="nav-item">
              <a data-bs-toggle="collapse" href="#farmsExamples"
                className={`nav-link text-white ${currentPage === 'farms' ? 'active bg-gradient-primary' : ''}`} aria-controls="farmsExamples" role="button" aria-expanded="true">
                <i className="material-symbols-rounded opacity-10">agriculture</i>
                <span className="nav-link-text ms-1 ps-1">Farms</span>
              </a>
              <div className="collapse show" id="farmsExamples">
                <ul className="nav">
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/farms/add">
                      <span className="sidenav-mini-icon">+</span>
                      <span className="sidenav-normal ms-1 ps-1">Add</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className={`nav-link text-white ${currentPage === 'farms' ? 'active' : ''}`} to="/farms">
                      <span className="sidenav-mini-icon"></span>
                      <span className="sidenav-normal ms-1 ps-1">All</span>
                    </Link>
                  </li>
                  {farms.length > 0 ? (
                    farms.map((farm) => {
                      if (!farm || !farm.farm_id || !farm.farm_name) {
                        console.warn('Invalid farm data:', farm);
                        return null;
                      }
                      // Navigate to ViewFarm page
                      const encodedFarmId = encodeURIComponent(farm.farm_id);
                      return (
                        <li className="nav-item" key={farm.farm_id}>
                          <Link className="nav-link text-white" to={`/farms/view/${encodedFarmId}`}>
                            <span className="sidenav-mini-icon"></span>
                            <span className="sidenav-normal ms-1 ps-1">{farm.farm_name}</span>
                          </Link>
                        </li>
                      );
                    })
                  ) : null}
                </ul>
              </div>
            </li>
            <li className="nav-item">
              <a data-bs-toggle="collapse" href="#samplesExamples"
                className={`nav-link text-white ${currentPage === 'samples' ? 'active bg-gradient-primary' : ''}`} aria-controls="samplesExamples" role="button" aria-expanded={currentPage === 'samples' ? 'true' : 'false'}>
                <i className="material-symbols-rounded opacity-10">photo_library</i>
                <span className="nav-link-text ms-1 ps-1">Samples</span>
              </a>
              <div className={`collapse ${currentPage === 'samples' ? 'show' : ''}`} id="samplesExamples">
                <ul className="nav">
                  <li className="nav-item">
                    <Link className={`nav-link text-white ${currentPage === 'upload' ? 'active' : ''}`} to="/upload">
                      <span className="sidenav-mini-icon">+</span>
                      <span className="sidenav-normal ms-1 ps-1">Add</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/samples?view=latest">
                      <span className="sidenav-mini-icon"></span>
                      <span className="sidenav-normal ms-1 ps-1">Latest</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/samples?view=all">
                      <span className="sidenav-mini-icon"></span>
                      <span className="sidenav-normal ms-1 ps-1">All</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </li>
            {/* Dynamic Links */}
            {dynamicLinks && dynamicLinks.map((link) => (
              <li className="nav-item" key={link.id}>
                <Link className="nav-link text-white" to={link.to}>
                  <i className="material-symbols-rounded opacity-10">{link.icon || 'display_settings'}</i>
                  <span className="nav-link-text ms-1 ps-1">{link.label}</span>
                </Link>
              </li>
            ))}
            <li className="nav-item">
              <Link className={`nav-link text-white ${currentPage === 'heatmap-demo' ? 'active bg-gradient-primary' : ''}`} to="/heatmap/demo">
                <i className="material-symbols-rounded opacity-10">whatshot</i>
                <span className="nav-link-text ms-1 ps-1">Infestation Heat Map</span>
              </Link>
            </li>
            <li className="nav-item">
              <a data-bs-toggle="collapse" href="#settingsExamples"
                className={`nav-link text-white ${currentPage === 'contact' || currentPage === 'stepupauth' ? 'active bg-gradient-primary' : ''}`} aria-controls="settingsExamples" role="button" aria-expanded={currentPage === 'contact' || currentPage === 'stepupauth' ? 'true' : 'false'}>
                <i className="material-symbols-rounded opacity-10">settings</i>
                <span className="nav-link-text ms-1 ps-1">Settings</span>
              </a>
              <div className="collapse" id="settingsExamples">
                <ul className="nav">
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/contact">
                      <i className="material-symbols-rounded opacity-10">contact_support</i>
                      <span className="sidenav-normal ms-1 ps-1">Contact</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link text-white" to="/stepupauth">
                      <i className="material-symbols-rounded opacity-10">security</i>
                      <span className="sidenav-normal ms-1 ps-1">Show Step Up Auth</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link text-white" href="#" onClick={(e) => {
                      e.preventDefault();
                      toggleShowAuthenticatorManager();
                    }}>
                      <i className="material-symbols-rounded opacity-10">vpn_key</i>
                      <span className="sidenav-normal ms-1 ps-1">Manage Authenticators</span>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link text-white" href="#" onClick={(e) => { e.preventDefault(); signOut(); }}>
                      <i className="material-symbols-rounded opacity-10">logout</i>
                      <span className="sidenav-normal ms-1 ps-1">Logout</span>
                    </a>
                  </li>
                </ul>
              </div>
            </li>
            {/* History Bar */}
            {/* <li className="nav-item">
              <HistoryBar />
            </li> */}

          </ul>
        </div>
        

      </aside>

      {/* Main Content */}
      <main className="main-content position-relative border-radius-lg">
        {/* Navbar */}
        <nav className="navbar navbar-main navbar-expand-lg position-sticky mt-2 top-1 px-0 py-1 mx-3 shadow-sm border-radius-lg z-index-sticky bg-white" id="navbarBlur" data-scroll="true">
          <div className="container-fluid py-1 px-2">
            {/* Sidebar toggler - only visible on xl screens and above */}
            <div className="sidenav-toggler sidenav-toggler-inner d-xl-block d-none">
              <button className="nav-link text-body p-0 border-0 bg-transparent" id="desktopHamburger">
                <div className="sidenav-toggler-inner">
                  <i className="sidenav-toggler-line"></i>
                  <i className="sidenav-toggler-line"></i>
                  <i className="sidenav-toggler-line"></i>
                </div>
              </button>
            </div>
            {/* Navbar toggler - only visible on screens smaller than lg, also toggles sidebar on mobile */}
            <button
              className="navbar-toggler d-lg-none"
              type="button"
              onClick={() => {
                setIsNavbarCollapsed(!isNavbarCollapsed);
                // Also toggle sidebar on mobile
                const body = document.body;
                const willOpen = !body.classList.contains('g-sidenav-pinned');
                body.classList.toggle('g-sidenav-pinned');
                if (willOpen) {
                  body.classList.remove('g-sidenav-hidden');
                  body.classList.add('g-sidenav-show');
                  setIsSidebarOpen(true);
                } else {
                  body.classList.remove('g-sidenav-show');
                  body.classList.add('g-sidenav-hidden');
                  setIsSidebarOpen(false);
                }
              }}
              aria-controls="navbar"
              aria-expanded={!isNavbarCollapsed}
              aria-label="Toggle navigation"
              style={{ marginLeft: '0.5rem' }}
            >
              <span className="navbar-toggler-icon">
                <i className="material-symbols-rounded">menu</i>
              </span>
            </button>
            <nav aria-label="breadcrumb" className="ps-4 d-none d-lg-block">
              <ol className="breadcrumb bg-transparent mb-0 p-0">
                <li className="breadcrumb-item text-sm">
                  <a className="opacity-5 text-deep-blue" href="">
                    {breadcrumb?.parent || 'Home'}
                  </a>
                </li>
                <li className="breadcrumb-item text-sm text-deep-blue active font-weight-bold" aria-current="page">
                  {breadcrumb?.current || 'Dashboard'}
                </li>
              </ol>
            </nav>
            <div className={`collapse navbar-collapse mt-sm-0 mt-2 me-md-0 me-sm-4 ${!isNavbarCollapsed ? 'show' : ''}`} id="navbar">
              <div className="ms-md-auto pe-md-3 d-flex flex-column flex-lg-row align-items-start align-items-lg-center">
                <nav aria-label="breadcrumb" className="ps-2 ps-lg-0 d-lg-none mb-2 mb-lg-0">
                  <ol className="breadcrumb bg-transparent mb-0 p-0">
                    <li className="breadcrumb-item text-sm">
                      <a className="opacity-5 text-deep-blue" href="">
                        {breadcrumb?.parent || 'Home'}
                      </a>
                    </li>
                    <li className="breadcrumb-item text-sm text-deep-blue active font-weight-bold" aria-current="page">
                      {breadcrumb?.current || 'Insights'}
                    </li>
                  </ol>
                </nav>
                <div className="input-group input-group-outline me-3 mt-2 mt-lg-3 w-100 w-lg-auto">
                  <input type="text" className="form-control" placeholder='Search' />
                </div>
                <ul className="navbar-nav d-flex flex-row align-items-center mt-2 mt-lg-0">
                  <li className="nav-item">
                    <a href="#" className="px-1 py-0 nav-link line-height-0 text-deep-blue">
                      <i className="material-symbols-rounded">account_circle</i>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a href="#" className="nav-link py-0 px-1 line-height-0 text-deep-blue">
                      <i className="material-symbols-rounded fixed-plugin-button-nav">settings</i>
                    </a>
                  </li>
                  <li className="nav-item dropdown py-0 pe-3">
                    <a href="#" className="nav-link py-0 px-1 position-relative line-height-0 text-deep-blue" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                      <i className="material-symbols-rounded">notifications</i>
                      <span className="position-absolute top-5 start-100 translate-middle badge rounded-pill bg-danger border border-white small py-1 px-2">
                        <span className="small">11</span>
                        <span className="visually-hidden">unread notifications</span>
                      </span>
                    </a>
                    <ul className="dropdown-menu dropdown-menu-end p-2 me-sm-n4" aria-labelledby="dropdownMenuButton">
                      <li className="mb-2">
                        <a className="dropdown-item border-radius-md" href="#">
                          <div className="d-flex align-items-center py-1">
                            <span className="material-symbols-rounded">email</span>
                            <div className="ms-2">
                              <h6 className="text-sm font-weight-normal my-auto">Check new messages</h6>
                            </div>
                          </div>
                        </a>
                      </li>
                      <li className="mb-2">
                        <a className="dropdown-item border-radius-md" href="#">
                          <div className="d-flex align-items-center py-1">
                            <span className="material-symbols-rounded">podcasts</span>
                            <div className="ms-2">
                              <h6 className="text-sm font-weight-normal my-auto">Manage podcast session</h6>
                            </div>
                          </div>
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item border-radius-md" href="#">
                          <div className="d-flex align-items-center py-1">
                            <span className="material-symbols-rounded">shopping_cart</span>
                            <div className="ms-2">
                              <h6 className="text-sm font-weight-normal my-auto">Payment successfully completed</h6>
                            </div>
                          </div>
                        </a>
                      </li>
                    </ul>
                  </li>
                  <li className="nav-item d-xl-none ps-3 d-flex align-items-center">
                    <button className="nav-link text-body p-0 border-0 bg-transparent" id="iconNavbarSidenav">
                      <div className="sidenav-toggler-inner">
                        <i className="sidenav-toggler-line"></i>
                        <i className="sidenav-toggler-line"></i>
                        <i className="sidenav-toggler-line"></i>
                      </div>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>
        {/* End Navbar */}

        {/* Page Content */}
        {children}
      </main>

      {/* Step Up Auth Modal */}
      {showStepUpAuth && <StepUpAuth />}
    </div>
  );
}; 